import {
  ChordAnchor,
  PreservedChordProFragment,
  StructuredArrangement,
  StructuredConversionResult,
  StructuredConversionWarning,
  StructuredLine,
  StructuredSection,
  StructuredSectionType,
  StructuredSong,
  StructuredSongMetadata,
  codePointOffsetToUtf16Index,
  sliceByCodePointOffset,
} from './structuredSong';

const SECTION_TYPES = new Set<StructuredSectionType>([
  'verse', 'chorus', 'pre_chorus', 'bridge', 'intro', 'outro',
  'interlude', 'tag', 'instrumental', 'custom',
]);
const METADATA = new Set([
  'title', 'subtitle', 'artist', 'composer', 'lyricist', 'copyright', 'ccli',
  'language', 'tags', 'publisher', 'license', 'source', 'album', 'year', 'duration',
  'key', 'tempo', 'time', 'capo',
]);
const METADATA_ALIASES: Record<string, string> = { t: 'title', st: 'subtitle', ccli_number: 'ccli', ccli_song: 'ccli' };
const CHORD_RE = /^(?:N\.?C\.?|[A-G](?:#|b)?(?:(?:maj|min|m|dim|aug|sus|add)\d*|\d+)*(?:\([^)]*\))?(?:\/[A-G](?:#|b)?)?)$/i;
const DIRECTIVE_RE = /^\{([^}:]+)(?::\s*([^}]*))?\}$/;

const hash = (value: string): string => {
  let h = 14695981039346656037n;
  const prime = 1099511628211n;
  const mask = 0xffffffffffffffffn;
  for (const byte of new TextEncoder().encode(value)) {
    h ^= BigInt(byte);
    h = (h * prime) & mask;
  }
  return h.toString(16).padStart(16, '0');
};

const stableKey = (prefix: string, ...parts: string[]): string => `${prefix}-${hash([prefix, ...parts].join('\x1f'))}`;

const sectionTypeFor = (name: string, label: string): StructuredSectionType => {
  if (name === 'start_of_custom') return 'custom';
  if (SECTION_TYPES.has(name as StructuredSectionType)) return name as StructuredSectionType;
  if (name === 'sov') return 'verse';
  if (name === 'soc') return 'chorus';
  if (name === 'sob') return 'bridge';
  if (name === 'soi') return 'intro';
  if (name === 'soo') return 'outro';
  return (label ? 'custom' : 'verse');
};

const startName = (type: StructuredSectionType): string => type === 'custom' ? 'custom' : type;
const endName = (type: StructuredSectionType): string => `end_of_${startName(type)}`;

const warning = (code: string, message: string, source_order?: number): StructuredConversionWarning => ({
  code, message, severity: 'warning', source_order,
});

const makeLine = (text: string, order: number, sectionKey: string): StructuredLine => ({
  stable_key: stableKey('line', sectionKey, String(order), text),
  order,
  text,
  chords: [],
  preserved_fragments: [],
});

const makeSection = (type: StructuredSectionType, label: string, order: number): StructuredSection => {
  const key = stableKey('section', type, '', String(order));
  return { stable_key: key, type, label, order, lines: [], preserved_fragments: [] };
};

const parseLine = (raw: string, order: number, section: StructuredSection, warnings: StructuredConversionWarning[]): StructuredLine => {
  let text = '';
  let cursor = 0;
  const chords: ChordAnchor[] = [];
  const preserved: PreservedChordProFragment[] = [];
  const tokenRe = /\[([^\]\n]+)]/g;
  let match: RegExpExecArray | null;
  while ((match = tokenRe.exec(raw))) {
    text += raw.slice(cursor, match.index);
    const name = match[1].trim();
    const position = Array.from(text).length;
    if (CHORD_RE.test(name)) chords.push({ position, name });
    else {
      preserved.push({ scope: 'line', raw: match[0], original_order: preserved.length, reason: 'unsupported_chord', section_stable_key: section.stable_key, line_order: order, position });
      warnings.push(warning('unsupported_chord', `Onbekend akkoord behouden: ${name}`));
    }
    cursor = tokenRe.lastIndex;
  }
  text += raw.slice(cursor);
  const line = makeLine(text, order, section.stable_key);
  line.chords = chords;
  line.preserved_fragments = preserved;
  return line;
};

export const parseStructuredSong = (source: string): StructuredConversionResult => {
  const normalized = source.replace(/\r\n?/g, '\n');
  const metadata: StructuredSongMetadata = {};
  const songFragments: PreservedChordProFragment[] = [];
  const warnings: StructuredConversionWarning[] = [];
  const sections: StructuredSection[] = [];
  let current: StructuredSection | null = null;
  let sourceOrder = 0;

  const addFragment = (fragment: PreservedChordProFragment) => {
    if (current) current.preserved_fragments.push(fragment);
    else songFragments.push(fragment);
  };

  normalized.split('\n').forEach((raw, lineIndex) => {
    const trimmed = raw.trim();
    const match = trimmed.match(DIRECTIVE_RE);
    if (match) {
      const originalName = match[1].trim().toLowerCase();
      const name = METADATA_ALIASES[originalName] || originalName;
      const value = (match[2] || '').trim();
      const startMatch = name.match(/^(?:start_of_|so)(.+)$/);
      const endMatch = name.match(/^(?:end_of_|eo)(.+)$/);
      if (METADATA.has(name)) {
        metadata[name] = value;
      } else if (startMatch) {
        const type = sectionTypeFor(startMatch[1], value);
        current = makeSection(type, value || type, sections.length);
        sections.push(current);
      } else if (endMatch) {
        if (!current) {
          songFragments.push({ scope: 'song', raw: trimmed, original_order: sourceOrder, reason: 'orphan section end' });
          warnings.push(warning('orphan_end_section', `Sluitdirective zonder geopende sectie: ${originalName}`, sourceOrder));
        }
        current = null;
      } else if (!value && ['verse', 'chorus', 'bridge', 'intro', 'outro'].includes(name)) {
        current = makeSection(name as StructuredSectionType, name, sections.length);
        sections.push(current);
      } else {
        addFragment({ scope: current ? 'section' : 'song', raw: trimmed, original_order: sourceOrder, reason: name === 'comment' ? 'comment' : 'unknown_directive', section_stable_key: current?.stable_key, line_order: current?.lines.length });
        warnings.push(warning('unknown_directive', `Geavanceerde ChordPro-inhoud behouden: ${name}`, sourceOrder));
      }
      sourceOrder += 1;
      return;
    }
    if (trimmed.startsWith('{')) {
      warnings.push(warning('malformed_directive', 'Ongeldige directive behouden als geavanceerde inhoud.', sourceOrder));
      if (current) current.lines.push(parseLine(raw, current.lines.length, current, warnings));
      else addFragment({ scope: 'song', raw, original_order: sourceOrder, reason: 'malformed_directive' });
    } else if (current) {
      current.lines.push(parseLine(raw, current.lines.length, current, warnings));
    } else if (raw.trim()) {
      const section = sections.length === 0 ? makeSection('custom', 'Tekst', 0) : null;
      if (section) {
        sections.push(section);
        current = section;
        current.lines.push(parseLine(raw, 0, current, warnings));
      } else {
        addFragment({ scope: 'song', raw, original_order: sourceOrder, reason: 'text_outside_section' });
      }
    }
    sourceOrder += 1;
  });

  const identityCounts = new Map<string, number>();
  sections.forEach((section) => {
    const firstText = section.lines.find((line) => line.text.trim())?.text || '';
    const identity = `${section.type}\x1f${firstText}`;
    const occurrence = identityCounts.get(identity) || 0;
    identityCounts.set(identity, occurrence + 1);
    const previousKey = section.stable_key;
    const nextKey = stableKey('section', section.type, firstText, String(occurrence));
    section.stable_key = nextKey;
    section.preserved_fragments.forEach((fragment) => { fragment.section_stable_key = nextKey; });
    section.lines.forEach((line) => {
      const oldLineKey = line.stable_key;
      line.stable_key = stableKey('line', nextKey, String(line.order), line.text);
      line.preserved_fragments.forEach((fragment) => { fragment.section_stable_key = nextKey; fragment.line_stable_key = line.stable_key; });
      if (oldLineKey !== line.stable_key) line.preserved_fragments = line.preserved_fragments.map((fragment) => ({ ...fragment, line_stable_key: line.stable_key }));
    });
    void previousKey;
  });
  const hasMetadata = Object.keys(metadata).length > 0;
  const hasContent = sections.some((section) => section.lines.some((line) => line.text.trim()));
  if (hasMetadata && !hasContent) warnings.push(warning('no_lyrics', 'Er is nog geen niet-lege liedtekst gevonden.', 0));
  sections.forEach((section) => {
    if (section.lines.some((line) => !line.text.trim())) {
      warnings.push(warning('empty_line', `Sectie '${section.label}' bevat lege regels.`, section.order));
    }
  });
  const arrangement: StructuredArrangement = {
    stable_key: 'arrangement-original',
    name: 'Origineel',
    default_key: metadata.key,
    bpm: metadata.tempo,
    meter: metadata.time,
    capo: metadata.capo,
    sections,
    preserved_fragments: [],
  };
  const song: StructuredSong = { metadata, arrangements: [arrangement], preserved_fragments: songFragments };
  const unsupported = !normalized.trim() || (!hasContent && !hasMetadata);
  const status = unsupported ? 'unsupported' : (warnings.length || songFragments.length || sections.some((section) => !section.lines.length) ? 'needs_review' : 'clean');
  if (unsupported) warnings.push(warning('no_song_content', 'Geen bruikbare structured song-inhoud gevonden.'));
  return { status, song, warnings, preserved_fragments: songFragments };
};

const serializeLine = (line: StructuredLine): string => {
  const anchors = [...line.chords].sort((a, b) => a.position - b.position);
  let output = '';
  let cursor = 0;
  anchors.forEach((anchor) => {
    const index = Math.max(0, Math.min(Array.from(line.text).length, anchor.position));
    output += sliceByCodePointOffset(line.text, cursor, index);
    output += `[${anchor.name}]`;
    cursor = index;
  });
  output += sliceByCodePointOffset(line.text, cursor);
  line.preserved_fragments.filter((fragment) => fragment.position !== undefined).forEach((fragment) => {
    const index = codePointOffsetToUtf16Index(output, fragment.position || 0);
    output = `${output.slice(0, index)}${fragment.raw}${output.slice(index)}`;
  });
  return output;
};

export const serializeStructuredSong = (song: StructuredSong, arrangementKey?: string): string => {
  const arrangement = song.arrangements.find((item) => item.stable_key === arrangementKey) || song.arrangements[0];
  const metadata: Array<[string, string | undefined]> = [
    ['title', song.metadata.title], ['subtitle', song.metadata.subtitle], ['artist', song.metadata.artist],
    ['composer', song.metadata.composer], ['lyricist', song.metadata.lyricist], ['copyright', song.metadata.copyright], ['ccli', song.metadata.ccli],
    ['key', arrangement?.default_key || song.metadata.key], ['tempo', arrangement?.bpm || song.metadata.tempo],
    ['time', arrangement?.meter || song.metadata.time], ['capo', arrangement?.capo || song.metadata.capo],
    ['language', song.metadata.language], ['album', song.metadata.album], ['year', song.metadata.year], ['duration', song.metadata.duration],
  ];
  const output = metadata.filter(([, value]) => value?.trim()).map(([name, value]) => `{${name}: ${value?.trim()}}`);
  song.preserved_fragments.filter((fragment) => fragment.scope === 'song').forEach((fragment) => output.push(fragment.raw));
  if (output.length) output.push('');
  (arrangement?.sections || []).forEach((section) => {
    output.push(`{start_of_${startName(section.type)}: ${section.label || section.type}}`);
    const fragmentsByLine = new Map<number, PreservedChordProFragment[]>();
    section.preserved_fragments.forEach((fragment) => {
      const lineOrder = fragment.line_order ?? 0;
      fragmentsByLine.set(lineOrder, [...(fragmentsByLine.get(lineOrder) || []), fragment]);
    });
    section.lines.forEach((line) => {
      (fragmentsByLine.get(line.order) || []).sort((a, b) => a.original_order - b.original_order).forEach((fragment) => output.push(fragment.raw));
      output.push(serializeLine(line));
    });
    (fragmentsByLine.get(section.lines.length) || []).sort((a, b) => a.original_order - b.original_order).forEach((fragment) => output.push(fragment.raw));
    output.push(`{${endName(section.type)}}`);
    output.push('');
  });
  return `${output.join('\n').replace(/\n+$/, '')}\n`;
};

export const updateLineChord = (line: StructuredLine, position: number, name: string): StructuredLine => ({
  ...line,
  chords: [...line.chords.filter((anchor) => anchor.position !== position), { position, name }].sort((a, b) => a.position - b.position),
});

export const removeLineChord = (line: StructuredLine, position: number): StructuredLine => ({
  ...line,
  chords: line.chords.filter((anchor) => anchor.position !== position),
});
